import { Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./cursor";
import type { Tree } from "@lezer/common";

const headingClasses: Record<string, string> = {
  ATXHeading1: "cm-md-heading1",
  ATXHeading2: "cm-md-heading2",
  ATXHeading3: "cm-md-heading3",
  ATXHeading4: "cm-md-heading4",
  ATXHeading5: "cm-md-heading5",
};

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

/**
 * Build decorations for ATX headings (h1-h3).
 *
 * When the cursor is NOT on the heading's line, the `# ` prefix is hidden
 * via a replace decoration and the heading text gets a styled mark decoration.
 * When the cursor IS on the line, no decorations are applied so the raw
 * markdown is visible for editing.
 */
export function buildHeadingDecorations(
  state: EditorState,
  tree: Tree,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  const cursorLine = cursor.cursorLine;

  tree.iterate({
    from: range.from,
    to: range.to,
    enter(node) {
      const cls = headingClasses[node.name];
      if (!cls) return;

      const headingLine = state.doc.lineAt(node.from).number;

      // If cursor is on this heading's line, show raw markdown
      if (cursorLine === headingLine) return;

      // Find the HeaderMark child (the `# ` prefix)
      let headerMarkFrom = -1;
      let headerMarkTo = -1;

      const childCursor = node.node.cursor();
      if (childCursor.firstChild()) {
        do {
          if (childCursor.name === "HeaderMark") {
            headerMarkFrom = childCursor.from;
            headerMarkTo = childCursor.to;
            break;
          }
        } while (childCursor.nextSibling());
      }

      if (headerMarkFrom === -1) return;

      // Hide the header mark (including the trailing space)
      // The HeaderMark covers just the `#` characters; the space after
      // is not part of the mark, so extend to headerMarkTo + 1 if there
      // is a space character there.
      let hideEnd = headerMarkTo;
      const nextChar = state.doc.sliceString(headerMarkTo, headerMarkTo + 1);
      if (nextChar === " ") {
        hideEnd = headerMarkTo + 1;
      }

      decorations.push({
        from: headerMarkFrom,
        to: hideEnd,
        decoration: Decoration.replace({}),
      });

      // Apply heading style to the remaining text
      if (hideEnd < node.to) {
        decorations.push({
          from: hideEnd,
          to: node.to,
          decoration: Decoration.mark({ class: cls }),
        });
      }
    },
  });
}
